/**
 * MelodimalMark — the official Melodimals mascot (musical animal face).
 *
 * Inline SVG (crisp scale, inherits currentColor where needed) reusable on
 * the home, splash and any brand spot. Without text: it is just the icon.
 */

import type { JSX } from "solid-js";

interface MelodimalMarkProps {
  /** Side in px or any CSS unit. */
  size?: number | string;
  class?: string;
  /** Accessible label; decorative by default (aria-hidden). */
  title?: string;
}

export function MelodimalMark(props: MelodimalMarkProps): JSX.Element {
  return (
    <svg
      viewBox="0 0 64 64"
      width={props.size ?? 64}
      height={props.size ?? 64}
      class={props.class}
      role={props.title === undefined ? "presentation" : "img"}
      aria-hidden={props.title === undefined ? "true" : undefined}
      aria-label={props.title}
    >
      <defs>
        <linearGradient id="mm-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="oklch(0.87 0.15 92)" />
          <stop offset="1" stop-color="oklch(0.78 0.16 60)" />
        </linearGradient>
        <radialGradient id="mm-cheek" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stop-color="oklch(0.7 0.17 25)" />
          <stop offset="1" stop-color="oklch(0.7 0.17 25 / 0)" />
        </radialGradient>
        {/* Top-down toy sheen for a glossy, pressable look. */}
        <linearGradient id="mm-gloss" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="oklch(1 0 0 / 0.5)" />
          <stop offset="0.45" stop-color="oklch(1 0 0 / 0)" />
        </linearGradient>
      </defs>
      {/* Hairline contrast ring so the badge keeps a crisp edge on both the
          cream light shell and the dark indigo (system dark mode) shell. */}
      <rect
        x="2"
        y="2"
        width="60"
        height="60"
        rx="18"
        fill="url(#mm-bg)"
        stroke="oklch(0.3 0.045 40 / 0.18)"
        stroke-width="1"
      />
      <rect x="6" y="5" width="52" height="26" rx="14" fill="url(#mm-gloss)" />
      <circle cx="18" cy="16" r="9" fill="oklch(0.78 0.16 60)" />
      <circle cx="46" cy="16" r="9" fill="oklch(0.78 0.16 60)" />
      <circle cx="18" cy="16" r="4.5" fill="oklch(0.87 0.15 92)" />
      <circle cx="46" cy="16" r="4.5" fill="oklch(0.87 0.15 92)" />
      <circle cx="32" cy="35" r="21" fill="oklch(0.98 0.02 85)" />
      <circle cx="20" cy="40" r="6" fill="url(#mm-cheek)" />
      <circle cx="44" cy="40" r="6" fill="url(#mm-cheek)" />
      <circle cx="25" cy="33" r="3.4" fill="oklch(0.3 0.045 40)" />
      <circle cx="39" cy="33" r="3.4" fill="oklch(0.3 0.045 40)" />
      <circle cx="26.1" cy="31.9" r="1.1" fill="oklch(1 0 0)" />
      <circle cx="40.1" cy="31.9" r="1.1" fill="oklch(1 0 0)" />
      <ellipse cx="32" cy="41.5" rx="3.2" ry="2.4" fill="oklch(0.78 0.16 60)" />
      <path
        d="M24 44 q8 8 16 0"
        fill="none"
        stroke="oklch(0.3 0.045 40)"
        stroke-width="2.6"
        stroke-linecap="round"
      />
      <g transform="translate(43 44)">
        <circle cx="0" cy="9" r="4.6" fill="oklch(0.66 0.15 300)" />
        <rect x="3.4" y="-3" width="2.4" height="12" rx="1.2" fill="oklch(0.66 0.15 300)" />
        <path d="M3.4 -3 q6 1 6 5 q0 -6 -6 -7 z" fill="oklch(0.66 0.15 300)" />
      </g>
    </svg>
  );
}
